import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';

declare global {
    interface Window {
        turnstile?: {
            render: (container: HTMLElement, options: Record<string, unknown>) => string;
            reset: (widgetId: string) => void;
            remove: (widgetId: string) => void;
        };
    }
}

export interface TurnstileWidgetHandle {
    reset: () => void;
}

interface TurnstileWidgetProps {
    onVerify: (token: string) => void;
    theme?: 'light' | 'dark' | 'auto';
}

// ══════════════════════════════════════════════
// TurnstileWidget — Wrapper بسيط حوالين Cloudflare Turnstile. السكريبت
// (challenges.cloudflare.com/turnstile/v0/api.js) بيتحمّل مرة واحدة بس من
// public/index.html، والكومبوننت ده بس بيعمل render للـ widget نفسه ويدي
// الـ parent الـ token عن طريق onVerify.
//
// ✅ التوكن اللي بيرجع هنا مجرد "إيصال" من المتصفح — التحقق الحقيقي إنه
// صحيح وماتسرّبش بيحصل على السيرفر (Supabase Edge Function اسمها
// verify-turnstile) قبل أي محاولة دخول فعلية. من غير الخطوة دي، أي حد
// يقدر يتجاوز الـ widget بالكامل عن طريق نداء الـ API مباشرة.
//
// ✅ الـ ref.reset() لازم تتنادى بعد أي محاولة فاشلة — توكنات Turnstile
// single-use، فلو المستخدم غلط الباسورد لازم widget جديد للمحاولة التانية.
// ══════════════════════════════════════════════
const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
    function TurnstileWidget({ onVerify, theme = 'light' }, ref) {
        const containerRef = useRef<HTMLDivElement | null>(null);
        const widgetIdRef = useRef<string | null>(null);
        const [scriptReady, setScriptReady] = useState(!!window.turnstile);

        useImperativeHandle(ref, () => ({
            reset: () => {
                if (widgetIdRef.current && window.turnstile) {
                    window.turnstile.reset(widgetIdRef.current);
                }
            },
        }));

        // السكريبت متحمّل async/defer من index.html — ممكن ياخد لحظة يوصل
        useEffect(() => {
            if (scriptReady) return;
            const check = setInterval(() => {
                if (window.turnstile) {
                    setScriptReady(true);
                    clearInterval(check);
                }
            }, 200);
            return () => clearInterval(check);
        }, [scriptReady]);

        useEffect(() => {
            if (!scriptReady || !containerRef.current || widgetIdRef.current || !window.turnstile) return;

            const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
            if (!siteKey) {
                console.error('REACT_APP_TURNSTILE_SITE_KEY غير مضبوط — الـ widget مش هيظهر');
                return;
            }

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: siteKey,
                theme,
                callback: (token: string) => onVerify(token),
                'expired-callback': () => onVerify(''),
                'error-callback': () => onVerify(''),
            });

            return () => {
                if (widgetIdRef.current && window.turnstile) {
                    window.turnstile.remove(widgetIdRef.current);
                    widgetIdRef.current = null;
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps -- scriptReady بس هو اللي لازم يعيد الـ render
        }, [scriptReady]);

        return <div ref={containerRef} style={{ margin: '4px 0' }} />;
    }
);

export default TurnstileWidget;
